/* eslint-disable import/order -- top-level require() is treated as an import */
import moment from 'moment';
import { LocaleData, registerLocale } from 'i18n-iso-countries';
import i18n from 'i18next';
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { toast } from 'react-toastify';
import { AppLanguage } from 'types/general';
import { StorageKeys } from './types/LocalStorage';
import { languageRequest } from './utils/language/language';
import { APP } from './config';

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

const englishCountries: LocaleData = require('i18n-iso-countries/langs/en.json');

const COUNTRIES_BY_LANGUAGE: Record<AppLanguage, LocaleData> = {
  bg: require('i18n-iso-countries/langs/bg.json'),
  bs: require('i18n-iso-countries/langs/bs.json'),
  cs: require('i18n-iso-countries/langs/cs.json'),
  da: require('i18n-iso-countries/langs/da.json'),
  de: require('i18n-iso-countries/langs/de.json'),
  el: require('i18n-iso-countries/langs/el.json'),
  en: englishCountries,
  gb: { ...englishCountries, locale: 'gb' },
  es: require('i18n-iso-countries/langs/es.json'),
  et: require('i18n-iso-countries/langs/et.json'),
  fi: require('i18n-iso-countries/langs/fi.json'),
  fr: require('i18n-iso-countries/langs/fr.json'),
  hr: require('i18n-iso-countries/langs/hr.json'),
  hu: require('i18n-iso-countries/langs/hu.json'),
  is: require('i18n-iso-countries/langs/is.json'),
  it: require('i18n-iso-countries/langs/it.json'),
  ka: require('i18n-iso-countries/langs/ka.json'),
  lt: require('i18n-iso-countries/langs/lt.json'),
  lv: require('i18n-iso-countries/langs/lv.json'),
  mk: require('i18n-iso-countries/langs/mk.json'),
  nl: require('i18n-iso-countries/langs/nl.json'),
  no: require('i18n-iso-countries/langs/no.json'),
  pl: require('i18n-iso-countries/langs/pl.json'),
  pt: require('i18n-iso-countries/langs/pt.json'),
  ro: require('i18n-iso-countries/langs/ro.json'),
  ru: require('i18n-iso-countries/langs/ru.json'),
  sk: require('i18n-iso-countries/langs/sk.json'),
  sl: require('i18n-iso-countries/langs/sl.json'),
  sq: require('i18n-iso-countries/langs/sq.json'),
  sr: require('i18n-iso-countries/langs/sr.json'),
  sv: require('i18n-iso-countries/langs/sv.json'),
  tr: require('i18n-iso-countries/langs/tr.json'),
  uk: require('i18n-iso-countries/langs/uk.json'),
};

const MOMENT_LOCALE_BY_LANGUAGE: Record<AppLanguage, string> = {
  bg: 'bg',
  bs: 'bs',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  en: 'en-gb',
  gb: 'en-gb',
  es: 'es',
  et: 'et',
  fi: 'fi',
  fr: 'fr',
  hr: 'hr',
  hu: 'hu',
  is: 'is',
  it: 'it',
  ka: 'ka',
  lt: 'lt',
  lv: 'lv',
  mk: 'mk',
  nl: 'nl',
  no: 'nb',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  ru: 'ru',
  sk: 'sk',
  sl: 'sl',
  sq: 'sq',
  sr: 'sr',
  sv: 'sv',
  tr: 'tr',
  uk: 'uk',
};

const backendOptions: HttpBackendOptions = {
  crossDomain: true,
  allowMultiLoading: true,
  loadPath: '{{lng}}|{{ns}}',
  request: languageRequest,
};

i18n
  .use(initReactI18next)
  .use(HttpBackend)
  .init(
    {
      backend: backendOptions,
      compatibilityJSON: 'v3',
      load: 'currentOnly',
      debug: false,
      lng: localStorage.getItem(StorageKeys.lang) || APP.locales.default,
      fallbackLng: false,
      ns: ['translations'],
      defaultNS: 'translations',
    },
    (err) => {
      if (err) {
        toast.error('Failed to load translations');
      }
    },
  )
  .then(() => {
    const stored = localStorage.getItem(StorageKeys.lang)?.toLowerCase();
    const country =
      COUNTRIES_BY_LANGUAGE[stored as AppLanguage] ||
      COUNTRIES_BY_LANGUAGE[APP.locales.default];
    registerLocale(country);
  });

i18n.on('languageChanged', async (lng) => {
  const language = lng.toLowerCase() as AppLanguage;
  const momentLocale =
    MOMENT_LOCALE_BY_LANGUAGE[language] || APP.locales.default;

  await import(
    `moment/locale/${momentLocale === 'en' ? 'en-gb' : momentLocale}`
  );
  if (language === 'gb' && !moment.locales().includes('gb')) {
    moment.defineLocale('gb', { parentLocale: 'en-gb' });
  }
  moment.locale(
    language === 'en' ? 'en' : language === 'gb' ? 'gb' : momentLocale,
  );

  const country =
    COUNTRIES_BY_LANGUAGE[language] ||
    COUNTRIES_BY_LANGUAGE[APP.locales.default];

  registerLocale(country);
});

export default i18n;
